import type { CompanyBranding, LogoPosition } from './CompanyBranding';
import { normalizeCompanyBranding } from './CompanyBranding';
import { contrastRatio, mixColors, WCAG_AA_NORMAL_TEXT } from './colors';
import { DEFAULT_FONT_KEY, fontStack } from './fonts';

/**
 * Thème de document dérivé d'une charte client. `active = false` (aucune charte, ou charte invalide)
 * ⇒ aucune variable, aucune classe : le rendu est strictement celui d'avant la fonctionnalité.
 */
export interface BrandTheme {
  readonly active: boolean;
  /** Data-URI du logo client (jamais d'URL) ou null. */
  readonly logoDataUri: string | null;
  readonly logoPosition: LogoPosition;
  /** Variables CSS posées sur l'élément racine du document (valeurs issues de couleurs validées). */
  readonly cssVars: Readonly<Record<string, string>>;
  /** Pile de polices (liste fermée) ou null si la police par défaut est conservée. */
  readonly fontStack: string | null;
  readonly primary: string;
  readonly headerText: string;
  readonly footerText: string;
}

export const INACTIVE_THEME: BrandTheme = {
  active: false,
  logoDataUri: null,
  logoPosition: 'left',
  cssVars: {},
  fontStack: null,
  primary: '',
  headerText: '',
  footerText: '',
};

const WHITE = '#FFFFFF';
const BLACK = '#000000';

/** Texte d'accent sur fond couleur principale : teinte claire du secondaire si lisible, sinon blanc. */
function accentOnPrimary(primary: string, secondary: string): string {
  const light = mixColors(secondary, WHITE, 0.55);
  return contrastRatio(light, primary) >= WCAG_AA_NORMAL_TEXT ? light : WHITE;
}

function softOnPrimary(primary: string): string {
  const soft = mixColors(WHITE, primary, 0.06);
  return contrastRatio(soft, primary) >= WCAG_AA_NORMAL_TEXT ? soft : WHITE;
}

function buildTheme(b: CompanyBranding): BrandTheme {
  const primary = b.primary_color;
  const secondary = b.secondary_color;
  const cssVars: Record<string, string> = {
    // Bulletin / facture / rapports (variables de nexxus-theme.css)
    '--marine': primary,
    '--teal': primary,
    '--cyan': secondary,
    '--menthe': softOnPrimary(primary),
    // Attestations, STC, bordereaux (variables .nx-attestation)
    '--nx-marine': primary,
    '--nx-marine-deep': mixColors(primary, BLACK, 0.45),
    '--nx-gold': secondary,
    '--nx-gold-light': accentOnPrimary(primary, secondary),
    '--nx-teal': secondary,
    '--nx-cream': mixColors(secondary, WHITE, 0.94),
    '--nx-sand': mixColors(secondary, WHITE, 0.82),
  };
  const logoDataUri = b.logo ? `data:${b.logo.mime};base64,${b.logo.data}` : null;
  return {
    active: true,
    logoDataUri,
    logoPosition: logoDataUri ? b.logo_position : 'left',
    cssVars,
    fontStack: b.font === DEFAULT_FONT_KEY ? null : fontStack(b.font),
    primary,
    headerText: b.header_text ?? '',
    footerText: b.footer_text ?? '',
  };
}

const cache = new WeakMap<object, BrandTheme>();

/**
 * Résout le thème d'une charte non fiable (lue en base, reçue du serveur…). Toute charte qui ne
 * repasse pas la validation complète est ignorée : le document retombe sur la charte par défaut
 * plutôt que d'afficher une valeur douteuse.
 */
export function resolveBrandTheme(branding: unknown): BrandTheme {
  if (branding === undefined || branding === null || typeof branding !== 'object') {
    return INACTIVE_THEME;
  }
  const hit = cache.get(branding);
  if (hit) return hit;
  const result = normalizeCompanyBranding(branding);
  const theme = result.ok ? buildTheme(result.value) : INACTIVE_THEME;
  cache.set(branding, theme);
  return theme;
}
